import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ChevronLeft, ChevronRight, CheckCircle, Loader2, FileText, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Step components
import StepCompanyDetails from './steps/StepCompanyDetails';
import StepBrandKit from './steps/StepBrandKit';
import StepBrandColors from './steps/StepBrandColors';
import StepDocuments from './steps/StepDocuments';
import StepWebsiteUrls from './steps/StepWebsiteUrls';
import StepDeveloperInfo from './steps/StepDeveloperInfo';
import StepForwardEmail from './steps/StepForwardEmail';
import StepSpecialisations from './steps/StepSpecialisations';
import StepDoNotMention from './steps/StepDoNotMention';
import StepReview from './steps/StepReview';

// Storage key prefix for client onboarding drafts
const ONBOARDING_DRAFT_KEY_PREFIX = 'client-onboarding-draft';

interface TenantData {
  id: string;
  name: string;
  industry: string | null;
  owner_email: string;
  status: string | null;
}

export interface OnboardingFormData {
  // Step 1 - Company details (read-only display)
  companyName: string;
  industry: string;
  
  // Step 2 - Brand kit
  useWebsiteAsDesignReference: boolean;
  logoFile: File | null;
  logoUrl: string;
  brandGuidelineFile: File | null;
  brandGuidelineUrl: string;
  
  // Step 3 - Brand colors & fonts
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFiles: File[];
  fontUrls: string[];
  
  // Step 4 - FAQs / documents
  documentFiles: File[];
  documentUrls: string[];
  
  // Step 5 - Website URLs
  primaryWebsiteUrl: string;
  additionalUrls: string[];
  
  // Step 6 - Developer info
  developerEmail: string;
  installScope: 'site-wide' | 'specific-pages';
  
  // Step 7 - Summary forward email
  summaryForwardEmail: string;
  
  // Step 8 - Specialisations
  specialisations: string[];
  specialisationsOther: string;
  
  // Step 9 - Do not mention
  doNotMention: string;
}

// Serializable version (excludes File objects for localStorage)
type SerializableOnboardingData = Omit<
  OnboardingFormData,
  'logoFile' | 'brandGuidelineFile' | 'fontFiles' | 'documentFiles'
>;

// Draft structure for localStorage
interface OnboardingDraft {
  data: SerializableOnboardingData;
  step: number;
  savedAt: number;
  companyName: string;
}

interface Props {
  tenant: TenantData;
  userId: string;
}

const STEPS = [
  { title: 'Company Details', description: 'Confirm your business information' },
  { title: 'Brand Kit', description: 'Upload your logo and brand guidelines' },
  { title: 'Brand Colors & Fonts', description: 'Define your brand identity' },
  { title: 'Documents & FAQs', description: 'Upload helpful resources' },
  { title: 'Website URLs', description: 'Tell us where to find your content' },
  { title: 'Developer Info', description: 'Who will install the widget?' },
  { title: 'Summary Emails', description: 'Where should we send conversation summaries?' },
  { title: 'Specialisations', description: 'What does your business focus on?' },
  { title: 'Topics to Avoid', description: 'What should your assistant not discuss?' },
  { title: 'Review & Submit', description: 'Confirm your information' },
];

const OnboardingWizardContent: React.FC<Props> = ({ tenant, userId }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedDraft, setSavedDraft] = useState<OnboardingDraft | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  const [formData, setFormData] = useState<OnboardingFormData>({
    companyName: tenant.name,
    industry: tenant.industry || 'accounting',
    useWebsiteAsDesignReference: false,
    logoFile: null,
    logoUrl: '',
    brandGuidelineFile: null,
    brandGuidelineUrl: '',
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    fontFiles: [],
    fontUrls: [],
    documentFiles: [],
    documentUrls: [],
    primaryWebsiteUrl: '',
    additionalUrls: [],
    developerEmail: '',
    installScope: 'site-wide',
    summaryForwardEmail: tenant.owner_email,
    specialisations: [],
    specialisationsOther: '',
    doNotMention: '',
  });

  // Storage helpers
  const getStorageKey = () => `${ONBOARDING_DRAFT_KEY_PREFIX}-${userId}-${tenant.id}`;

  const saveDraft = () => {
    if (!userId || !tenant.id) return;
    const { logoFile, brandGuidelineFile, fontFiles, documentFiles, ...serializable } = formData;
    const draft: OnboardingDraft = {
      data: serializable as SerializableOnboardingData,
      step: currentStep,
      savedAt: Date.now(),
      companyName: formData.companyName || tenant.name,
    };
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(draft));
      setLastSaved(new Date());
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  };

  const loadDraft = (): OnboardingDraft | null => {
    if (!userId || !tenant.id) return null;
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const clearDraft = () => {
    if (!userId || !tenant.id) return;
    try {
      localStorage.removeItem(getStorageKey());
      setLastSaved(null);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  // Check for existing draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft && draft.step > 0) {
      setSavedDraft(draft);
      setShowRestorePrompt(true);
    }
  }, [userId, tenant.id]);

  // Auto-save on data/step changes (debounced)
  useEffect(() => {
    if (!userId || !tenant.id) return;
    // Only save if there's meaningful progress (past first step or has data)
    if (currentStep === 0 && !formData.primaryWebsiteUrl) return;
    
    const timer = setTimeout(() => {
      saveDraft();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [formData, currentStep, userId, tenant.id]);

  const handleRestoreDraft = () => {
    if (savedDraft) {
      setFormData({ 
        ...formData,
        ...savedDraft.data,
        // Reset file fields
        logoFile: null,
        brandGuidelineFile: null,
        fontFiles: [],
        documentFiles: [],
      });
      setCurrentStep(savedDraft.step);
      toast({
        title: 'Draft Restored',
        description: 'Please re-upload any files you had previously selected.',
      });
    }
    setShowRestorePrompt(false);
    setSavedDraft(null);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowRestorePrompt(false);
    setSavedDraft(null);
  };

  const updateFormData = (updates: Partial<OnboardingFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 0: return true; // Company details - read only
      case 1: return true; // Brand kit - optional
      case 2: return true; // Brand colors - optional
      case 3: return true; // Documents - optional
      case 4: return formData.primaryWebsiteUrl.trim().length > 0; // Website URL required
      case 5: return formData.developerEmail.trim().length > 0; // Developer email required
      case 6: return formData.summaryForwardEmail.trim().length > 0; // Summary email required
      case 7: return true; // Specialisations - optional
      case 8: return true; // Do not mention - optional
      case 9: return true; // Review
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const uploadFile = async (file: File, assetType: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${tenant.id}/${assetType}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('tenant-assets')
      .upload(filePath, file);

    if (uploadError) {
      throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
    }

    const { data: { publicUrl } } = supabase.storage
      .from('tenant-assets')
      .getPublicUrl(filePath);

    // Store asset record
    await supabase.from('tenant_assets').insert({
      tenant_id: tenant.id,
      asset_type: assetType,
      file_name: file.name,
      file_url: publicUrl,
      file_size: file.size,
      mime_type: file.type,
      uploaded_by: userId,
    });

    return publicUrl;
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      // Upload files
      let logoUrl = formData.logoUrl;
      let brandGuidelineUrl = formData.brandGuidelineUrl;
      const fontUrls: string[] = [...formData.fontUrls];
      const documentUrls: string[] = [...formData.documentUrls];

      if (formData.logoFile) {
        logoUrl = await uploadFile(formData.logoFile, 'logo');
      }

      if (formData.brandGuidelineFile) {
        brandGuidelineUrl = await uploadFile(formData.brandGuidelineFile, 'brand_guideline');
      }

      for (const fontFile of formData.fontFiles) {
        const url = await uploadFile(fontFile, 'font');
        fontUrls.push(url);
      }

      for (const docFile of formData.documentFiles) {
        const url = await uploadFile(docFile, 'document');
        documentUrls.push(url);
      }

      // Prepare specialisations array
      const specialisations = [...formData.specialisations];
      if (formData.specialisationsOther.trim()) {
        specialisations.push(formData.specialisationsOther.trim());
      }

      // Update tenant record
      const { error: updateError } = await supabase
        .from('advisory_tenants')
        .update({
          status: 'building',
          website_url: formData.primaryWebsiteUrl,
          additional_urls: formData.additionalUrls.filter(u => u.trim()),
          developer_email: formData.developerEmail,
          install_scope: formData.installScope,
          summary_forward_email: formData.summaryForwardEmail,
          specialisations: specialisations,
          do_not_mention: formData.doNotMention,
          use_website_as_design_reference: formData.useWebsiteAsDesignReference,
          brand_colors: {
            primary: formData.primaryColor,
            secondary: formData.secondaryColor,
            accent: formData.accentColor,
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', tenant.id);

      if (updateError) {
        throw new Error(`Failed to update tenant: ${updateError.message}`);
      }

      // Trigger notification email
      try {
        await supabase.functions.invoke('notify-intake-submission', {
          body: {
            tenantId: tenant.id,
            tenantName: tenant.name,
            industry: tenant.industry,
            ownerEmail: tenant.owner_email,
            developerEmail: formData.developerEmail,
            websiteUrl: formData.primaryWebsiteUrl,
            specialisations: specialisations,
          },
        });
      } catch (notifyError) {
        console.error('Failed to send notification:', notifyError);
        // Don't fail the submission if notification fails
      }

      // Trigger website crawl for knowledge extraction
      try {
        console.log('Starting website crawl for tenant:', tenant.id);
        const crawlResponse = await supabase.functions.invoke('crawl-tenant-website', {
          body: {
            tenantId: tenant.id,
            websiteUrl: formData.primaryWebsiteUrl,
            tenantName: tenant.name,
          },
        });
        
        if (crawlResponse.error) {
          console.error('Crawl function error:', crawlResponse.error);
        } else {
          console.log('Website crawl completed:', crawlResponse.data);
        }
      } catch (crawlError) {
        console.error('Failed to crawl website:', crawlError);
        // Don't fail the submission if crawl fails - it can be retried
      }

      // Clear draft on successful submission
      clearDraft();

      toast({
        title: 'Submission Complete!',
        description: 'Our team will begin building your assistant. We\'ll be in touch soon!',
      });

      // Redirect to success page or dashboard
      navigate('/assistants/checkout-success');
    } catch (error) {
      console.error('Submission error:', error);
      toast({
        title: 'Submission Failed',
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0: return <StepCompanyDetails data={formData} />;
      case 1: return <StepBrandKit data={formData} updateData={updateFormData} />;
      case 2: return <StepBrandColors data={formData} updateData={updateFormData} />;
      case 3: return <StepDocuments data={formData} updateData={updateFormData} />;
      case 4: return <StepWebsiteUrls data={formData} updateData={updateFormData} />;
      case 5: return <StepDeveloperInfo data={formData} updateData={updateFormData} />;
      case 6: return <StepForwardEmail data={formData} updateData={updateFormData} />;
      case 7: return <StepSpecialisations data={formData} updateData={updateFormData} />;
      case 8: return <StepDoNotMention data={formData} updateData={updateFormData} />;
      case 9: return <StepReview data={formData} />;
      default: return null;
    }
  };

  return (
    <>
      {/* Restore Draft Prompt */}
      <AlertDialog open={showRestorePrompt} onOpenChange={setShowRestorePrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Draft Found
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>You have unsaved onboarding progress:</p>
              <p className="font-medium text-foreground">
                "{savedDraft?.companyName}" - Step {(savedDraft?.step ?? 0) + 1}
              </p>
              <p className="text-xs text-muted-foreground">
                Last edited: {savedDraft ? formatDistanceToNow(savedDraft.savedAt, { addSuffix: true }) : ''}
              </p>
              <p className="text-amber-600 text-sm mt-2">
                Note: Previously uploaded files will need to be re-uploaded.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardDraft}>Start Fresh</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreDraft}>Continue Draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Welcome, {tenant.name}!
          </h1>
          <p className="text-muted-foreground">
            Complete this wizard to help us build your perfect AI assistant.
          </p>
          {lastSaved && currentStep > 0 && (
            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1 mt-2">
              <Clock className="h-3 w-3" />
              Draft saved
            </p>
          )}
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Step {currentStep + 1} of {STEPS.length}</span>
            <span>{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="mt-4">
            <h2 className="text-lg font-semibold text-foreground">{STEPS[currentStep].title}</h2>
            <p className="text-sm text-muted-foreground">{STEPS[currentStep].description}</p>
          </div>
        </div>

      {/* Step Content */}
      <div className="bg-card border border-border rounded-xl p-6 mb-8 min-h-[300px]">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentStep === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>

        {currentStep < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-brand-teal hover:bg-brand-teal/90"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Submit
              </>
            )}
          </Button>
        )}
      </div>
      </div>
    </>
  );
};

export default OnboardingWizardContent;
